import React, { useState, Component } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ShoppingBag, Star, Plus, Minus, Heart, Share2, Check, ChevronRight, Droplet, Sparkles, Sun, Moon, RefreshCw, Clock, ThumbsUp, Shield } from 'lucide-react';
import { allRiaProducts } from './RiaSkincare';
import { useCart } from '../context/CartContext';
// Product details data
const productDetails = {
  301: {
    size: '30ml',
    benefits: ['Deeply hydrates and plumps skin', 'Reduces appearance of fine lines', 'Improves skin texture and radiance', 'Provides long-lasting moisture'],
    ingredients: 'Aqua, Sodium Hyaluronate, Seaweed Extract, Glycerin, Natural Preservatives',
    keyIngredients: ['Hyaluronic Acid: Provides intense hydration and plumps skin', 'Seaweed Extract: Rich in minerals and antioxidants', 'Glycerin: Maintains skin moisture balance'],
    howToUse: ['Cleanse your face thoroughly', 'Apply 2-3 drops to clean, damp skin', 'Gently pat and press into skin', 'Follow with moisturizer if needed'],
    color: 'teal',
    ritualSteps: [{
      title: 'Prep your skin',
      description: 'Begin with a clean canvas. Wash your face with our Gentle Foaming Cleanser and pat skin until slightly damp. The damp skin creates the perfect environment for hyaluronic acid to work its magic, drawing moisture into the deeper layers of your skin.',
      icon: 'droplet',
      time: 'Morning & Evening'
    }, {
      title: 'Apply the serum',
      description: "Dispense 2-3 drops onto fingertips. Our hyaluronic formula works best on slightly damp skin to lock in maximum hydration. The lightweight molecular structure allows for deep penetration, delivering hydration to multiple layers of your skin where it's needed most.",
      icon: 'sparkles',
      time: 'Morning & Evening'
    }, {
      title: "Press, don't rub",
      description: 'Gently press and pat the serum into your skin, working from the center outward. Allow 30 seconds for absorption. This pressing technique enhances circulation and ensures the active ingredients penetrate effectively without causing friction that can irritate delicate facial skin.',
      icon: 'hands',
      time: 'Morning & Evening'
    }, {
      title: 'Seal with moisture',
      description: 'Follow with our Seaweed Restore Face Cream to lock in the serum and create a protective moisture barrier. This final step is crucial as it seals in the hydrating benefits of the serum while providing additional nourishment and protection against environmental stressors throughout the day.',
      icon: 'shield',
      time: 'Morning & Evening'
    }],
    ritualIntro: "Our Hyaluronic Serum is the cornerstone of effective hydration. This ritual harnesses the power of molecular hydration science to deliver moisture deep into your skin's layers, creating a plumping effect that diminishes fine lines and creates a dewy, youthful complexion.",
    scienceNote: "Hyaluronic acid can hold up to 1000 times its weight in water, making it nature's most powerful hydrator. Our unique formulation combines different molecular weights of hyaluronic acid to target multiple layers of the skin simultaneously."
  },
  302: {
    size: '30ml',
    benefits: ['Nourishes and revitalizes skin', 'Provides essential fatty acids', 'Enhances natural skin radiance', 'Lightweight, non-greasy formula'],
    ingredients: 'Jojoba Oil, Seaweed Extract, Vitamin E, Natural Essential Oils',
    keyIngredients: ["Jojoba Oil: Mimics skin's natural sebum for optimal absorption", 'Seaweed Extract: Provides minerals and antioxidants', 'Vitamin E: Protects against environmental damage'],
    howToUse: ['Use on clean, slightly damp skin', 'Apply 3-4 drops to face and neck', 'Massage gently until absorbed', 'Use morning and evening'],
    color: 'emerald',
    ritualSteps: [{
      title: 'Start with clean skin',
      description: 'Apply to freshly cleansed skin. For best results, use after our Seaweed Hyaluronic Serum while skin is still slightly damp. This sequence allows the oil to trap the hydration from the serum, maximizing the benefits of both products and creating the perfect foundation for nourishment.',
      icon: 'droplet',
      time: 'Morning & Evening'
    }, {
      title: 'Warm the oil',
      description: 'Dispense 3-4 drops into palms and rub hands together to warm the oil, releasing its aromatic properties and preparing it for optimal absorption. This warming action activates the botanical compounds and helps the oil penetrate more effectively, while the gentle aroma provides an aromatherapeutic benefit to calm your senses.',
      icon: 'sun',
      time: 'Morning & Evening'
    }, {
      title: 'Apply with intention',
      description: 'Press palms gently onto face and neck, then use fingertips to massage in upward, circular motions until fully absorbed. This intentional application technique stimulates circulation, encourages lymphatic drainage, and ensures the nutrients reach all areas of your face evenly while providing a moment of mindful self-care.',
      icon: 'refresh',
      time: 'Morning & Evening'
    }, {
      title: 'Layer strategically',
      description: 'In the morning, follow with sunscreen. In the evening, use as your final step to seal in moisture and nutrients overnight. This strategic layering approach ensures your skin receives appropriate protection during the day and maximum regenerative benefits at night when your skin naturally enters its repair cycle.',
      icon: 'moon',
      time: 'Morning: Before SPF | Evening: Final step'
    }],
    ritualIntro: "Our Facial Oil Elixir transforms daily skincare into a luxurious ritual that nourishes your skin and senses simultaneously. This carefully crafted sequence maximizes the oil's ability to deliver essential nutrients while providing a moment of aromatic tranquility in your day.",
    scienceNote: "The molecular structure of our jojoba oil closely resembles your skin's natural sebum, allowing for deeper penetration and better absorption than typical facial oils. This similarity helps balance oil production while delivering nutrients exactly where they're needed."
  },
  303: {
    size: '100ml',
    benefits: ['Gently exfoliates dead skin cells', 'Reveals smoother, brighter skin', 'Unclogs pores naturally', 'Suitable for sensitive skin'],
    ingredients: 'Natural Exfoliating Particles, Seaweed Extract, Aloe Vera, Glycerin',
    keyIngredients: ['Natural Exfoliants: Gently remove dead skin cells', 'Seaweed Extract: Provides nourishing minerals', 'Aloe Vera: Soothes and calms skin'],
    howToUse: ['Apply to damp skin', 'Massage gently in circular motions', 'Rinse thoroughly with warm water', 'Use 2-3 times per week'],
    color: 'blue',
    ritualSteps: [{
      title: 'Prepare your skin',
      description: 'Dampen face with lukewarm water. The water activates the natural enzymes in the polish for optimal exfoliation. This precise temperature balance opens pores without shocking the skin, creating the ideal environment for gentle yet effective removal of dead skin cells and impurities.',
      icon: 'droplet',
      time: '2-3 times weekly'
    }, {
      title: 'Apply with care',
      description: 'Dispense a small amount (about the size of a blueberry) onto fingertips and apply to face, avoiding the delicate eye area. This measured approach ensures you apply just the right amount of product to effectively exfoliate without overwhelming your skin or causing microtears from excessive scrubbing.',
      icon: 'sparkles',
      time: 'Evening preferred'
    }, {
      title: 'Gentle circular motions',
      description: 'Using light pressure, massage in small circles focusing on areas prone to congestion (T-zone, chin). Continue for 30-60 seconds. This technique ensures the natural exfoliating particles effectively lift away dead skin cells while the circular motion stimulates microcirculation for a healthy glow.',
      icon: 'refresh',
      time: 'No more than 1 minute'
    }, {
      title: 'Rinse & follow up',
      description: 'Rinse thoroughly with lukewarm water and pat skin dry. Follow immediately with our Seaweed Hyaluronic Serum for enhanced absorption. This timing is crucial—freshly exfoliated skin has temporarily enhanced permeability, allowing the subsequent hydrating products to penetrate more deeply and effectively.',
      icon: 'thumbs-up',
      time: 'Complete ritual within 5 minutes'
    }],
    ritualIntro: "Our Gentle Exfoliating Polish transforms the essential step of exfoliation into a rejuvenating ritual that respects your skin's natural balance. This carefully timed sequence removes dull surface cells while preparing your skin to receive maximum benefits from the products that follow.",
    scienceNote: "Unlike harsh physical exfoliants, our polish uses biodegradable spherical particles that roll across the skin's surface, effectively removing dead cells without causing micro-tears. This approach stimulates renewal while maintaining your skin's protective barrier."
  }
};
// Default details function
const getDefaultDetails = product => {
  if (!product) return {
    size: 'N/A',
    benefits: ['Coming soon'],
    ingredients: 'Ingredients list coming soon',
    keyIngredients: ['Details coming soon'],
    howToUse: ['Usage instructions coming soon'],
    color: 'emerald',
    ritualSteps: [{
      title: 'Step 1',
      description: 'More details coming soon',
      icon: 'info',
      time: 'TBD'
    }]
  };
  return {
    size: '50ml',
    benefits: ['Nourishes and hydrates skin', 'Made with natural ingredients', 'Suitable for daily use', 'Cruelty-free formula'],
    ingredients: 'Natural botanical extracts, essential oils, and plant-based ingredients',
    keyIngredients: ['Botanical Extracts: Provide natural nourishment', 'Essential Oils: Offer therapeutic benefits', 'Plant-based Ingredients: Gentle and effective'],
    howToUse: ['Apply to clean skin', 'Massage gently until absorbed', 'Use as directed', 'For external use only'],
    color: 'emerald',
    ritualSteps: [{
      title: 'Prepare',
      description: 'Start with clean, dry skin for best results',
      icon: 'droplet',
      time: 'As needed'
    }, {
      title: 'Apply',
      description: 'Use as directed on the product packaging',
      icon: 'sparkles',
      time: 'As needed'
    }, {
      title: 'Finish',
      description: 'Follow with other products in your routine as needed',
      icon: 'thumbs-up',
      time: 'As needed'
    }]
  };
};
// Helper function to get color classes based on product
const getColorClasses = (product, details) => {
  const colorMap = {
    teal: {
      primary: 'bg-teal-500',
      light: 'bg-teal-50',
      text: 'text-teal-600',
      border: 'border-teal-200',
      hover: 'hover:bg-teal-600',
      lightHover: 'hover:bg-teal-100',
      gradient: 'from-teal-50 to-teal-100'
    },
    emerald: {
      primary: 'bg-emerald-500',
      light: 'bg-emerald-50',
      text: 'text-emerald-600',
      border: 'border-emerald-200',
      hover: 'hover:bg-emerald-600',
      lightHover: 'hover:bg-emerald-100',
      gradient: 'from-emerald-50 to-emerald-100'
    },
    blue: {
      primary: 'bg-blue-500',
      light: 'bg-blue-50',
      text: 'text-blue-600',
      border: 'border-blue-200',
      hover: 'hover:bg-blue-600',
      lightHover: 'hover:bg-blue-100',
      gradient: 'from-blue-50 to-blue-100'
    }
  };
  return colorMap[details?.color || 'emerald'];
};
const SingleRiaProductPage = () => {
  const {
    productId
  } = useParams();
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState('details');
  const {
    addToCart
  } = useCart();
  // Find the product based on the ID from the URL
  const product = allRiaProducts.find(p => p.id === parseInt(productId || '0'));
  // Get product details
  const details = productDetails[product?.id] || getDefaultDetails(product);
  const colorClasses = getColorClasses(product, details);
  const decreaseQuantity = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };
  const increaseQuantity = () => {
    setQuantity(quantity + 1);
  };
  const handleAddToCart = () => {
    if (product) {
      for (let i = 0; i < quantity; i++) {
        addToCart({
          ...product,
          priceValue: parseInt(product.price.replace(/[^\d]/g, '')) || 0
        });
      }
    }
  };
  const getRelatedProducts = currentProduct => {
    if (!currentProduct) return [];
    let categoryStart, categoryEnd;
    if (currentProduct.id >= 300) {
      categoryStart = 300;
      categoryEnd = 399;
    } else if (currentProduct.id >= 200) {
      categoryStart = 200;
      categoryEnd = 299;
    } else {
      categoryStart = 100;
      categoryEnd = 199;
    }
    return allRiaProducts.filter(p => p.id >= categoryStart && p.id <= categoryEnd && p.id !== currentProduct.id).slice(0, 3);
  };
  const relatedProducts = product ? getRelatedProducts(product) : [];
  // Helper function to get the right icon component
  const getIconComponent = iconName => {
    switch (iconName) {
      case 'droplet':
        return <Droplet className="h-5 w-5" data-id="element-1298" />;
      case 'sparkles':
        return <Sparkles className="h-5 w-5" data-id="element-1299" />;
      case 'sun':
        return <Sun className="h-5 w-5" data-id="element-1300" />;
      case 'moon':
        return <Moon className="h-5 w-5" data-id="element-1301" />;
      case 'refresh':
        return <RefreshCw className="h-5 w-5" data-id="element-1302" />;
      case 'clock':
        return <Clock className="h-5 w-5" data-id="element-1303" />;
      case 'thumbs-up':
        return <ThumbsUp className="h-5 w-5" data-id="element-1304" />;
      case 'shield':
        return <Shield className="h-5 w-5" data-id="element-1305" />;
      case 'hands':
        return <div className="flex items-center justify-center" data-id="element-1306">
            <span className="text-lg" data-id="element-1307">👐</span>
          </div>;
      default:
        return <Check className="h-5 w-5" data-id="element-1308" />;
    }
  };
  if (!product) {
    return <div className="min-h-screen bg-white py-16 px-4 flex items-center justify-center" data-id="element-1309">
        <div className="max-w-md mx-auto text-center" data-id="element-1310">
          <h1 className="text-xl font-light text-gray-800 mb-4" data-id="element-1311">
            Product Not Found
          </h1>
          <p className="text-gray-500 mb-6" data-id="element-1312">
            The product you're looking for doesn't exist or has been removed.
          </p>
          <Link to="/ria-skincare" className="inline-flex items-center px-5 py-2 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors duration-300" data-id="element-1313">
            <ArrowLeft className="mr-2 h-4 w-4" data-id="element-1314" />
            Return to Collection
          </Link>
        </div>
      </div>;
  }
  return <div className={`bg-gradient-to-b ${colorClasses.gradient} w-full`} data-id="element-1315">
      {/* Micro breadcrumb navigation */}
      <div className="py-3 border-b border-gray-100 bg-white" data-id="element-1316">
        <div className="container mx-auto px-4 max-w-6xl" data-id="element-1317">
          <nav className="flex items-center text-xs" data-id="element-1318">
            <Link to="/" className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-1319">
              Home
            </Link>
            <ChevronRight className="mx-1 h-3 w-3 text-gray-300" data-id="element-1320" />
            <Link to="/ria-skincare" className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-1321">
              Ria
            </Link>
            <ChevronRight className="mx-1 h-3 w-3 text-gray-300" data-id="element-1322" />
            <span className="text-gray-600 truncate max-w-[150px]" data-id="element-1323">
              {product.name}
            </span>
          </nav>
        </div>
      </div>

      {/* Product showcase */}
      <section className="py-12" data-id="element-1324">
        <div className="container mx-auto px-4 max-w-6xl" data-id="element-1325">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start" data-id="element-1326">
            {/* Product image */}
            <div data-id="element-1327">
              <div className="aspect-square bg-white rounded-lg overflow-hidden shadow-md" data-id="element-1328">
                <img src={product.image} alt={product.name} className="w-full h-full object-cover" onError={e => {
                e.currentTarget.src = 'https://images.unsplash.com/photo-1620916566398-39f1143ab7be?q=80&w=600&auto=format&fit=crop';
              }} data-id="element-1329" />
              </div>
              <div className={`mt-4 p-4 rounded-lg ${colorClasses.light} ${colorClasses.border} border`} data-id="element-1330">
                <div className="flex items-center" data-id="element-1331">
                  <div className={`w-8 h-8 rounded-full ${colorClasses.primary} flex items-center justify-center mr-3`} data-id="element-1332">
                    <Sparkles className="h-4 w-4 text-white" data-id="element-1333" />
                  </div>
                  <p className="text-sm text-gray-700" data-id="element-1334">
                    <span className="font-medium" data-id="element-1335">Ria Promise:</span> 100%
                    natural ingredients, ethically sourced and cruelty-free
                  </p>
                </div>
              </div>
            </div>
            {/* Product information */}
            <div data-id="element-1336">
              <div className="mb-8" data-id="element-1337">
                <span className={`text-xs uppercase tracking-wider px-3 py-1 rounded-full ${colorClasses.light} ${colorClasses.text}`} data-id="element-1338">
                  Ria Collection
                </span>
                <h1 className="text-2xl font-normal text-gray-900 mt-2 mb-4" data-id="element-1339">
                  {product.name}
                </h1>
                <p className="text-xl text-gray-900 font-light mb-6" data-id="element-1340">
                  {product.price}
                </p>
                <div className={`w-16 h-1 ${colorClasses.primary} rounded-full mb-6`} data-id="element-1341"></div>
                <p className="text-gray-600 text-sm leading-relaxed mb-8" data-id="element-1342">
                  {product.description}
                </p>
                {/* Key benefits */}
                <div className="mb-8" data-id="element-1343">
                  <h3 className="text-sm text-gray-900 mb-4" data-id="element-1344">Key Benefits</h3>
                  <ul className="space-y-2" data-id="element-1345">
                    {details.benefits.slice(0, 4).map((benefit, index) => <li key={index} className="flex items-start" data-id="element-1346">
                        <Check className={`h-4 w-4 ${colorClasses.text} mr-2 mt-0.5`} data-id="element-1347" />
                        <span className="text-gray-600 text-sm" data-id="element-1348">{benefit}</span>
                      </li>)}
                  </ul>
                </div>
                {/* Size information */}
                <div className="mb-8" data-id="element-1349">
                  <h3 className="text-sm text-gray-900 mb-2" data-id="element-1350">Size</h3>
                  <span className={`inline-block text-sm ${colorClasses.text} px-3 py-1 rounded-full ${colorClasses.light} ${colorClasses.border} border`} data-id="element-1351">
                    {details.size}
                  </span>
                </div>
                {/* Purchase options */}
                <div data-id="element-1352">
                  <div className="flex items-center mb-6" data-id="element-1353">
                    {/* Quantity selector */}
                    <div className="flex items-center" data-id="element-1354">
                      <span className="text-sm text-gray-600 mr-4" data-id="element-1355">
                        Quantity
                      </span>
                      <div className="flex items-center border border-gray-200 rounded-md" data-id="element-1356">
                        <button onClick={decreaseQuantity} className="p-2 text-gray-400 hover:text-gray-600 transition-colors" data-id="element-1357">
                          <Minus className="h-3 w-3" data-id="element-1358" />
                        </button>
                        <div className="w-8 text-center text-gray-800 text-sm" data-id="element-1359">
                          {quantity}
                        </div>
                        <button onClick={increaseQuantity} className="p-2 text-gray-400 hover:text-gray-600 transition-colors" data-id="element-1360">
                          <Plus className="h-3 w-3" data-id="element-1361" />
                        </button>
                      </div>
                    </div>
                    {/* Wishlist */}
                    <button className="ml-auto p-2 text-gray-400 hover:text-gray-600 transition-colors" data-id="element-1362">
                      <Heart className="h-5 w-5" data-id="element-1363" />
                    </button>
                    {/* Share */}
                    <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors" data-id="element-1364">
                      <Share2 className="h-5 w-5" data-id="element-1365" />
                    </button>
                  </div>
                  {/* CTA button */}
                  <button onClick={handleAddToCart} className={`w-full px-4 py-3 ${colorClasses.primary} text-white text-sm ${colorClasses.hover} transition-colors duration-300 flex items-center justify-center rounded-md shadow-md`} data-id="element-1366">
                    <span data-id="element-1367">Add to Cart</span>
                    <ShoppingBag className="ml-2 h-4 w-4" data-id="element-1368" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tabs for product details */}
      <section className="py-12 border-t border-gray-100 bg-white" data-id="element-1369">
        <div className="container mx-auto px-4 max-w-4xl" data-id="element-1370">
          {/* Tab navigation */}
          <div className="flex justify-center mb-12 border-b border-gray-200" data-id="element-1371">
            {['details', 'ingredients', 'how to use', 'reviews'].map(tab => <button key={tab} onClick={() => setActiveTab(tab.replace(' ', ''))} className={`px-6 py-3 text-sm transition-all duration-300 ${activeTab === tab.replace(' ', '') ? `text-gray-900 border-b-2 ${colorClasses.text} border-current` : 'text-gray-400 hover:text-gray-600'}`} data-id="element-1372">
                {tab}
              </button>)}
          </div>
          {/* Tab content */}
          <div data-id="element-1373">
            {activeTab === 'details' && <div className="space-y-8" data-id="element-1374">
                <p className="text-gray-600 text-sm leading-relaxed" data-id="element-1375">
                  {product.description}
                </p>
                <div data-id="element-1376">
                  <h3 className="text-sm text-gray-900 mb-4" data-id="element-1377">Key Benefits</h3>
                  <ul className="space-y-3" data-id="element-1378">
                    {details.benefits.map((benefit, index) => <li key={index} className="flex items-start" data-id="element-1379">
                        <Check className={`h-4 w-4 ${colorClasses.text} mr-2 mt-0.5`} data-id="element-1380" />
                        <p className="text-gray-600 text-sm" data-id="element-1381">{benefit}</p>
                      </li>)}
                  </ul>
                </div>
                <div data-id="element-1382">
                  <h3 className="text-sm text-gray-900 mb-4" data-id="element-1383">Our Commitment</h3>
                  <p className="text-gray-600 text-sm mb-4" data-id="element-1384">
                    At Ria, we believe in the power of nature. Our products are
                    formulated with care, using only the finest natural
                    ingredients.
                  </p>
                  <div className="grid grid-cols-2 gap-y-2 text-sm text-gray-600" data-id="element-1385">
                    <div className="flex items-center" data-id="element-1386">
                      <div className={`w-1 h-1 rounded-full ${colorClasses.primary} mr-2`} data-id="element-1387"></div>
                      <span data-id="element-1388">Ethically sourced</span>
                    </div>
                    <div className="flex items-center" data-id="element-1389">
                      <div className={`w-1 h-1 rounded-full ${colorClasses.primary} mr-2`} data-id="element-1390"></div>
                      <span data-id="element-1391">Cruelty-free</span>
                    </div>
                    <div className="flex items-center" data-id="element-1392">
                      <div className={`w-1 h-1 rounded-full ${colorClasses.primary} mr-2`} data-id="element-1393"></div>
                      <span data-id="element-1394">Eco-conscious</span>
                    </div>
                    <div className="flex items-center" data-id="element-1395">
                      <div className={`w-1 h-1 rounded-full ${colorClasses.primary} mr-2`} data-id="element-1396"></div>
                      <span data-id="element-1397">No harmful chemicals</span>
                    </div>
                  </div>
                </div>
              </div>}
            {activeTab === 'ingredients' && <div className="space-y-8" data-id="element-1398">
                <div data-id="element-1399">
                  <p className="text-gray-600 text-sm italic" data-id="element-1400">
                    {details.ingredients}
                  </p>
                </div>
                <div data-id="element-1401">
                  <h3 className="text-sm text-gray-900 mb-4" data-id="element-1402">
                    Key Ingredients
                  </h3>
                  <div className="space-y-4" data-id="element-1403">
                    {details.keyIngredients.map((ingredient, index) => {
                  const [name, description] = ingredient.split(': ');
                  return <div key={index} className={`border border-gray-100 ${colorClasses.light} rounded-lg p-4`} data-id="element-1404">
                          <p className={`${colorClasses.text} text-sm font-medium mb-1`} data-id="element-1405">
                            {name}
                          </p>
                          <p className="text-gray-600 text-sm" data-id="element-1406">{description}</p>
                        </div>;
                })}
                  </div>
                </div>
              </div>}
            {activeTab === 'howtouse' && <div className="space-y-8" data-id="element-1407">
                <div className={`p-6 ${colorClasses.light} rounded-lg mb-8`} data-id="element-1408">
                  <h2 className={`text-lg font-medium ${colorClasses.text} mb-2`} data-id="element-1409">
                    The Ria Ritual
                  </h2>
                  <p className="text-gray-600 text-sm mb-4" data-id="element-1410">
                    Transform your skincare routine into a mindful ritual with
                    our step-by-step guide. Each Ria product is designed to be
                    used with intention, creating moments of self-care in your
                    day while delivering powerful results through our
                    scientifically formulated natural ingredients.
                  </p>
                  {details.ritualIntro && <p className="text-gray-700 text-sm italic" data-id="element-1411">
                      {details.ritualIntro}
                    </p>}
                </div>
                {details.scienceNote && <div className={`p-5 border ${colorClasses.border} rounded-lg mb-8 bg-white`} data-id="element-1412">
                    <div className="flex" data-id="element-1413">
                      <div className={`w-10 h-10 rounded-full ${colorClasses.light} flex items-center justify-center mr-3 flex-shrink-0`} data-id="element-1414">
                        <Sparkles className={`h-5 w-5 ${colorClasses.text}`} data-id="element-1415" />
                      </div>
                      <div data-id="element-1416">
                        <h3 className="text-sm font-medium text-gray-900 mb-1" data-id="element-1417">
                          The Science Behind the Ritual
                        </h3>
                        <p className="text-xs text-gray-600" data-id="element-1418">
                          {details.scienceNote}
                        </p>
                      </div>
                    </div>
                  </div>}
                <div className="space-y-6" data-id="element-1419">
                  {details.ritualSteps.map((step, index) => <div key={index} className="flex" data-id="element-1420">
                      <div className={`flex-shrink-0 w-12 h-12 ${colorClasses.light} rounded-full flex items-center justify-center mr-4 ${colorClasses.text}`} data-id="element-1421">
                        {getIconComponent(step.icon)}
                      </div>
                      <div className="pt-1" data-id="element-1422">
                        <div className="flex items-center mb-1" data-id="element-1423">
                          <h3 className="text-gray-900 font-medium text-sm" data-id="element-1424">
                            {step.title}
                          </h3>
                          <span className={`ml-3 text-xs ${colorClasses.text} px-2 py-0.5 rounded-full ${colorClasses.light}`} data-id="element-1425">
                            {step.time}
                          </span>
                        </div>
                        <p className="text-gray-600 text-sm" data-id="element-1426">
                          {step.description}
                        </p>
                      </div>
                    </div>)}
                </div>
                <div className={`border border-gray-100 rounded-lg p-6 mt-8 bg-gradient-to-r ${colorClasses.gradient}`} data-id="element-1427">
                  <h3 className="text-sm text-gray-900 mb-4" data-id="element-1428">Pro Tips</h3>
                  <ul className="space-y-3 text-sm text-gray-600" data-id="element-1429">
                    <li className="flex items-start" data-id="element-1430">
                      <div className={`w-5 h-5 rounded-full ${colorClasses.primary} flex items-center justify-center mr-2 mt-0.5`} data-id="element-1431">
                        <Check className="h-3 w-3 text-white" data-id="element-1432" />
                      </div>
                      <span data-id="element-1433">
                        For enhanced results, incorporate this product into your
                        daily ritual consistently for at least 28 days—this
                        aligns with your skin's natural renewal cycle for
                        visible transformation.
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-1434">
                      <div className={`w-5 h-5 rounded-full ${colorClasses.primary} flex items-center justify-center mr-2 mt-0.5`} data-id="element-1435">
                        <Check className="h-3 w-3 text-white" data-id="element-1436" />
                      </div>
                      <span data-id="element-1437">
                        Store in a cool, dry place away from direct sunlight to
                        preserve the potency of active ingredients. Natural
                        formulations are sensitive to heat and light, which can
                        degrade their effectiveness.
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-1438">
                      <div className={`w-5 h-5 rounded-full ${colorClasses.primary} flex items-center justify-center mr-2 mt-0.5`} data-id="element-1439">
                        <Check className="h-3 w-3 text-white" data-id="element-1440" />
                      </div>
                      <span data-id="element-1441">
                        Layer with other Ria products in the correct order for
                        maximum benefits: cleanse, tone, serum, oil, moisturize.
                        This sequence ensures each product can perform optimally
                        without interference.
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-1442">
                      <div className={`w-5 h-5 rounded-full ${colorClasses.primary} flex items-center justify-center mr-2 mt-0.5`} data-id="element-1443">
                        <Check className="h-3 w-3 text-white" data-id="element-1444" />
                      </div>
                      <span data-id="element-1445">
                        Take a moment to inhale the natural aroma before
                        application to enjoy the aromatherapy benefits. This
                        mindful pause activates the olfactory system, enhancing
                        the mood-boosting effects of our botanical ingredients.
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-1446">
                      <div className={`w-5 h-5 rounded-full ${colorClasses.primary} flex items-center justify-center mr-2 mt-0.5`} data-id="element-1447">
                        <Check className="h-3 w-3 text-white" data-id="element-1448" />
                      </div>
                      <span data-id="element-1449">
                        For an enhanced experience, perform your ritual in a
                        calm environment free from distractions. These few
                        minutes of focused self-care can become a therapeutic
                        ritual that benefits both skin and mind.
                      </span>
                    </li>
                  </ul>
                </div>
                <div className="p-5 bg-white rounded-lg border border-gray-200 mt-8" data-id="element-1450">
                  <h3 className="text-sm font-medium text-gray-900 mb-3" data-id="element-1451">
                    Why Ritual Matters
                  </h3>
                  <p className="text-sm text-gray-600 mb-4" data-id="element-1452">
                    At Ria, we believe skincare is more than just applying
                    products—it's an opportunity for mindful self-care. Our
                    ritual approach combines traditional wisdom with modern
                    science to create a holistic experience that:
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm" data-id="element-1453">
                    <div className="flex items-start" data-id="element-1454">
                      <div className={`w-6 h-6 rounded-full ${colorClasses.light} flex items-center justify-center mr-2 mt-0.5`} data-id="element-1455">
                        <Check className={`h-3 w-3 ${colorClasses.text}`} data-id="element-1456" />
                      </div>
                      <p className="text-gray-600" data-id="element-1457">
                        Enhances product effectiveness through proper
                        application techniques
                      </p>
                    </div>
                    <div className="flex items-start" data-id="element-1458">
                      <div className={`w-6 h-6 rounded-full ${colorClasses.light} flex items-center justify-center mr-2 mt-0.5`} data-id="element-1459">
                        <Check className={`h-3 w-3 ${colorClasses.text}`} data-id="element-1460" />
                      </div>
                      <p className="text-gray-600" data-id="element-1461">
                        Creates moments of calm in your day through sensory
                        engagement
                      </p>
                    </div>
                    <div className="flex items-start" data-id="element-1462">
                      <div className={`w-6 h-6 rounded-full ${colorClasses.light} flex items-center justify-center mr-2 mt-0.5`} data-id="element-1463">
                        <Check className={`h-3 w-3 ${colorClasses.text}`} data-id="element-1464" />
                      </div>
                      <p className="text-gray-600" data-id="element-1465">
                        Respects your skin's natural processes and biological
                        rhythms
                      </p>
                    </div>
                    <div className="flex items-start" data-id="element-1466">
                      <div className={`w-6 h-6 rounded-full ${colorClasses.light} flex items-center justify-center mr-2 mt-0.5`} data-id="element-1467">
                        <Check className={`h-3 w-3 ${colorClasses.text}`} data-id="element-1468" />
                      </div>
                      <p className="text-gray-600" data-id="element-1469">
                        Transforms routine into an act of self-care and
                        mindfulness
                      </p>
                    </div>
                  </div>
                </div>
              </div>}
            {activeTab === 'reviews' && <div className="space-y-8 text-center" data-id="element-1470">
                <div className="flex items-center justify-center" data-id="element-1471">
                  <div className="flex items-center mr-2" data-id="element-1472">
                    {[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 text-gray-300" data-id="element-1473" />)}
                  </div>
                  <span className="text-gray-600 text-sm" data-id="element-1474">No reviews yet</span>
                </div>
                <div data-id="element-1475">
                  <p className="text-gray-600 mb-6 text-sm" data-id="element-1476">
                    This product is waiting for its first review. Share your
                    experience and help others discover the benefits.
                  </p>
                  <button className={`px-6 py-2 border ${colorClasses.border} ${colorClasses.text} text-sm ${colorClasses.lightHover} transition-colors duration-300 rounded-md`} data-id="element-1477">
                    Write First Review
                  </button>
                </div>
              </div>}
          </div>
        </div>
      </section>

      {/* Related products */}
      {relatedProducts.length > 0 && <section className={`py-12 border-t border-gray-100 ${colorClasses.light}`} data-id="element-1478">
          <div className="container mx-auto px-4 max-w-6xl" data-id="element-1479">
            <h2 className="text-lg text-gray-900 mb-8 text-center" data-id="element-1480">
              Complete Your Ritual
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-8" data-id="element-1481">
              {relatedProducts.map(relatedProduct => <Link key={relatedProduct.id} to={`/ria-skincare/product/${relatedProduct.id}`} className="group" data-id="element-1482">
                  <div className="bg-white rounded-lg shadow-sm overflow-hidden" data-id="element-1483">
                    <div className="aspect-square overflow-hidden" data-id="element-1484">
                      <img src={relatedProduct.image} alt={relatedProduct.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" onError={e => {
                  e.currentTarget.src = 'https://images.unsplash.com/photo-1620916566398-39f1143ab7be?q=80&w=600&auto=format&fit=crop';
                }} data-id="element-1485" />
                    </div>
                    <div className="p-4" data-id="element-1486">
                      <h3 className="text-gray-900 text-sm mb-1" data-id="element-1487">
                        {relatedProduct.name}
                      </h3>
                      <p className={`${colorClasses.text} text-sm font-medium`} data-id="element-1488">
                        {relatedProduct.price}
                      </p>
                    </div>
                  </div>
                </Link>)}
            </div>
          </div>
        </section>}

      {/* Newsletter section */}
      <section className="py-12 border-t border-gray-100 bg-white" data-id="element-1489">
        <div className="container mx-auto px-4 max-w-xl text-center" data-id="element-1490">
          <h2 className="text-lg text-gray-900 mb-2" data-id="element-1491">Join Our Community</h2>
          <p className="text-sm text-gray-600 mb-6" data-id="element-1492">
            Subscribe for exclusive offers and skincare tips
          </p>
          <div className="flex" data-id="element-1493">
            <input type="email" placeholder="Your email address" className="flex-1 px-4 py-2 border border-gray-200 border-r-0 text-sm focus:outline-none rounded-l-md" data-id="element-1494" />
            <button className={`px-6 py-2 ${colorClasses.primary} text-white text-sm ${colorClasses.hover} transition-colors duration-300 rounded-r-md`} data-id="element-1495">
              Subscribe
            </button>
          </div>
        </div>
      </section>
    </div>;
};
export default SingleRiaProductPage;